// YesNo: a recorded fact (pyro fitted, customer owned) as one key that flips between Yes and No.
// Yes takes the done green with a green square, No stays neutral with a grey one, and the word
// always says which. A setting that acts at once is the kit Switch, not this.
// docs/operations/design-standard.md, "Controls".

import React from 'react';
import Tooltip from './Tooltip';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, TOUCH } from './motion';

export interface YesNoProps {
  value: boolean;
  onChange: (next: boolean) => void;
  /** The field it records, as the accessible name: "Pyro fitted". */
  label: string;
  /** `sm` in table cells and dense rails, `md` by default. */
  size?: 'sm' | 'md';
  disabled?: boolean;
  /** Saving: a screen reader hears it, and presses wait until it settles. */
  busy?: boolean;
  /** Shown, not changed: the key steps down, keeps its value readable, and says "Read-only". */
  readOnly?: boolean;
  className?: string;
}

const SIZE = {
  sm: 'h-7 gap-1.5 px-2 text-xs',
  md: 'h-8 gap-2 px-2.5 text-[13px] leading-[18px]',
};
const DOT = { sm: 'h-2 w-2', md: 'h-2.5 w-2.5' };

// The good tone's pair from categoryColors (green mark at 15% under its ink), as Button's `good`;
// No is the neutral pair at the same strength, whose ink clears Lc 60 where on-surface-variant
// would not. Literal, because Tailwind builds only what it reads.
const GREEN_INK = 'text-[color:oklch(0.83_0.13_148)]';
const NEUTRAL_INK = 'text-[color:oklch(0.83_0_0)]';
const FACE = {
  yes: `bg-[color:oklch(0.7_0.16_148_/_0.15)] ${GREEN_INK}`,
  no: `bg-[color:oklch(0.7_0_0_/_0.15)] ${NEUTRAL_INK}`,
};
const DOT_FILL = { yes: 'bg-[color:oklch(0.83_0.13_148)]', no: 'bg-[color:oklch(0.7_0_0)]' };

export const YesNo: React.FC<YesNoProps> = ({
  value, onChange, label, size = 'md', disabled = false, busy = false, readOnly = false, className = '',
}) => {
  const said = value ? 'yes' : 'no';
  const off = disabled && !readOnly;
  const face = off
    ? 'bg-surface-container text-on-surface-disabled'
    : readOnly
      ? `bg-surface-container ${value ? GREEN_INK : NEUTRAL_INK}`
      : `${FACE[said]} ${busy ? 'cursor-progress' : ''}`;
  const tip = readOnly ? 'Read-only' : off ? '' : busy ? 'Saving' : value ? 'Change to No' : 'Change to Yes';
  return (
    <Tooltip label={tip}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={value}
        aria-busy={busy || undefined}
        disabled={disabled || readOnly}
        onClick={() => { if (!busy) onChange(!value); }}
        className={`inline-flex flex-none select-none items-center rounded-control font-medium disabled:pointer-events-none ${SIZE[size]} ${face} ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH} ${className}`}
      >
        <span aria-hidden="true" className={`flex-none rounded-inner transition-[background-color] duration-quick ease-emphasized ${DOT[size]} ${off ? 'bg-on-surface-disabled' : DOT_FILL[said]}`} />
        {/* Both words share one cell, so the key keeps its width as the value flips and the new word fades in. */}
        <span className="inline-grid">
          <span aria-hidden={!value || undefined} className={`[grid-area:1/1] transition-opacity duration-quick ease-emphasized ${value ? 'opacity-100' : 'opacity-0'}`}>Yes</span>
          <span aria-hidden={value || undefined} className={`[grid-area:1/1] transition-opacity duration-quick ease-emphasized ${value ? 'opacity-0' : 'opacity-100'}`}>No</span>
        </span>
      </button>
    </Tooltip>
  );
};

export default YesNo;
